/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import debug from 'debug';
import { fromNullable } from 'fp-ts/lib/Option';
import { setFocusId } from 'sdi/app';
import { DIV, H2, SPAN } from 'sdi/components/elements';
import geocoder, {
    layerActionSelectOnMap,
    layerActionTable,
} from 'sdi/components/geocoder';
import { clearServiceResponse } from 'sdi/geocoder/events';
import tr, { fromRecord } from 'sdi/locale';
import { create, IMapOptions, multiSelectOptions } from 'sdi/map';
import { mapRightTools, renderScaleline, storeOlMap } from 'sdi/map/controls';
import { dispatchK } from 'sdi/shape/functions';
import { ILayerInfo, MessageRecord } from 'sdi/source';
import {
    selectTableView,
    setCurrentLayer,
    setLayout,
    setMapBaseLayer,
    setTableFilter,
    signalReadyMap,
} from '../events/app';
import { addFeature } from '../events/harvest';
import {
    dispatchView,
    endMark,
    measureEvents,
    scalelineEvents,
    selectFeatures,
    setExtractCollection,
    setInteraction,
    setPointerPosition,
    setPrintResponse,
    startMark,
    startTracker,
    stopPointerPosition,
    trackerEvents,
    updateLoading,
    viewEvents,
} from '../events/map';
import {
    getCurrentBaseLayer,
    getCurrentBaseLayerOpt,
    getCurrentSelection,
    getMapInfo,
    getMapLayers,
} from '../queries/app';
import { withoutBookmarks } from '../queries/bookmark';
import { getGeometryType } from '../queries/harvest';
import {
    getInteraction,
    getInteractionMode,
    getLoading,
    getPrintRequest,
    getScaleLine,
    getView,
    HarvestLayerName,
} from '../queries/map';

const logger = debug('sdi:comp/map');
export const mapName = 'main-view';
const getMapLayerInfo = () =>
    fromNullable(getMapInfo()).fold([], m => getMapLayers(m));

const options: IMapOptions = {
    element: null,
    getBaseLayer: getCurrentBaseLayer,
    getMapInfo,
    getMapLayerInfo,
    getView,

    updateView: viewEvents.updateMapView,
    setScaleLine: scalelineEvents.setScaleLine,
    setLoading: updateLoading,
};

let mapSetTarget: (t: Element | null) => void;
let mapUpdate: () => void;

const attachMap = () => (element: HTMLElement | null) => {
    // logger(`attachMap ${typeof element}`);
    if (!mapUpdate) {
        const {
            update,
            setTarget,
            selectable,
            measurable,
            trackable,
            extractable,
            markable,
            printable,
            positionable,
            editable,
            andThen,
        } = create(mapName, { ...options, element });
        mapSetTarget = setTarget;
        mapUpdate = update;
        signalReadyMap();

        selectable(
            multiSelectOptions({
                selectFeatures,
                getSelected: getCurrentSelection,
            }),
            getInteraction,
        );

        measurable(
            {
                updateMeasureCoordinates:
                    measureEvents.updateMeasureCoordinates,
                stopMeasuring: measureEvents.stopMeasure,
            },
            getInteraction,
        );

        trackable(
            {
                resetTrack: trackerEvents.resetTrack,
                setCenter: center =>
                    viewEvents.updateMapView({ dirty: 'geo', center }),
                updateTrack: trackerEvents.updateTrack,
            },
            getInteraction,
        );

        extractable(
            {
                setCollection: setExtractCollection,
            },
            getInteraction,
        );

        markable({ startMark, endMark }, getInteraction);

        // highlightable(getSelected); // TODO

        printable(
            {
                getRequest: getPrintRequest,
                setResponse: setPrintResponse,
            },
            getInteraction,
        );

        positionable(
            {
                setPosition: setPointerPosition,
                stopPosition: stopPointerPosition,
            },
            getInteraction,
        );

        editable(
            {
                addFeature,
                setGeometry: () => void 0,
                getCurrentLayerId: () => HarvestLayerName,
                getGeometryType: () => getGeometryType().getOrElse('Polygon'),
            },
            getInteraction,
        );

        andThen(map => {
            storeOlMap(map);
            map.on('singleclick', () => clearServiceResponse());
            map.on('moveend', () => clearServiceResponse());
        });
    }
    if (element) {
        mapSetTarget(element);
    } else {
        mapSetTarget(null);
    }
};

const renderLoading = (ms: Readonly<MessageRecord[]>) =>
    DIV(
        {
            className: `loading-layer-wrapper ${
                ms.length === 0 ? 'hidden' : ''
            }`,
        },
        H2({}, tr.view('loadingData')),
        ms.map(r =>
            DIV(
                {
                    className: 'loading-layer',
                    key: fromRecord(r),
                },
                SPAN({ className: 'loader-spinner' }),
                fromRecord(r),
            ),
        ),
    );

const render = () => {
    if (mapUpdate) {
        mapUpdate();
    }

    // FIXME - this is a hell of a hack - pm
    const withPrintSize = (() => {
        const i = getInteraction();
        const req = getPrintRequest();
        if (i.label === 'print' && req !== null) {
            return {
                style: {
                    width: Math.round((req.width * req.resolution) / 25.4),
                    height: Math.round((req.height * req.resolution) / 25.4),
                },
            };
        }
        return {};
    })();

    const selectFilteredTable = layerActionTable(
        (info: ILayerInfo, field: string, searchQuery: string) => {
            setCurrentLayer(info.id);
            selectTableView();
            setTableFilter(field, searchQuery);
            setLayout('map-fs');
            setFocusId(`table-download-button-${info.id}`);
        },
    );

    const selectOnMap = layerActionSelectOnMap((layerId, featureIds) =>
        selectFeatures(featureIds.map(featureId => ({ layerId, featureId }))),
    );

    return DIV(
        {
            className: `map-wrapper map-interaction-${getInteractionMode()}`,
            'aria-label': `${tr.core('map')}`,
        },

        DIV({
            key: '__this_is_a_unique_map__',
            className: 'map',
            tabIndex: 0,
            ref: attachMap(),
            ...withPrintSize,
        }),
        renderLoading(getLoading()),
        geocoder({
            getMapLayerInfos: () => withoutBookmarks(getMapLayerInfo()),
            termConfig: { setView: dispatchView, setInteraction },
            layerActions: [selectFilteredTable, selectOnMap],
        }),
        mapRightTools(dispatchK('port/map/view'), getView, startTracker, {
            setBaseLayer: setMapBaseLayer,
            getBaseLayer: () => getCurrentBaseLayerOpt().map(bl => bl.id),
        }),
        renderScaleline(getScaleLine()),
    );
};

export default render;

logger('loaded');
